<template>
  <div class="analytics-detailed">
    <AnalyticsControls :type="AnalyticsType.ADVANCED" />
    <AnalyticsAdvancedStatsPanel
      v-if="hasValue"
      class="analytics-detailed__stats-panel"
      :data="analyticsStatsData"
    />
    <AnalyticsLineChart
      v-if="hasValue"
      class="analytics-detailed__line-chart"
      :title="analyticsLineChartTitle"
      subTitle="Отображение аккаунтов на графике:"
      :platformsTitle="platformsTitle"
      :type="AnalyticsType.ADVANCED"
      :lineDataChart="lineChartData"
      :platforms="platforms"
    />
    <AnalyticsPlaceholder
      v-if="!hasValue && !isFetchingAnalytics"
      class="analytics-advanced__placeholder"
    >
      {{
        !selectedServices.length
          ? 'Чтобы работать с Аналитикой, выберите площадку и рекламный аккаунт'
          : 'Нет данных за выбранный период'
      }}
    </AnalyticsPlaceholder>
  </div>
</template>
<script>
import AnalyticsControls from '@/views/Analytics/AnalyticsControls';
import AnalyticsAdvancedStatsPanel from '@/views/Analytics/AnalyticsAdvanced/AnalyticsAdvancedStatsPanel';
import AnalyticsLineChart from '@/views/Analytics/AnalyticsLineChart';
import {
  AnalyticsType,
  KPIKeys,
  KPIText,
  ShortKPIText,
  KPIKeysBackendMapper,
  GenitiveKPIText,
} from '@/utils/common';
import { getServiceColor, getServiceName, ServiceType } from '@/utils/services';
import { getRandomHexColor } from '@/utils/helpers';
import moment from 'moment';
import AnalyticsPlaceholder from '@/views/Analytics/AnalyticsPlaceholder';
import numberWithSpacesCeilRound from '@/mixin/numberWithSpaces';
import { formatCurrency } from '@/utils/helpers';

export default {
  name: 'AnalyticsDetailed',
  components: {
    AnalyticsControls,
    AnalyticsAdvancedStatsPanel,
    AnalyticsLineChart,
    AnalyticsPlaceholder,
  },
  mixins: [numberWithSpacesCeilRound],
  data() {
    return {
      AnalyticsType,
      colorsMap: new Map(),
      MAX_ITEMS: 10,
    };
  },
  computed: {
    onlyTelegramSelected() {
      return (
        this.selectedServices.length === 1 &&
        this.selectedServices[0].toUpperCase() === ServiceType.TELEGRAM
      );
    },
    onlyGoogleSelected() {
      return (
        this.selectedServices.length === 1 &&
        this.selectedServices[0].toUpperCase() === ServiceType.GOOGLE
      );
    },
    currencyRates() {
      return this.$store.state.Auth.currencyRates;
    },
    activeAccount() {
      return this.$store.state.Auth.activeAccount;
    },
    isFetchingAnalytics() {
      return this.$store.state.Analytics[AnalyticsType.ADVANCED].isLoading;
    },
    advancedAnalyticsData() {
      return (
        {
          ...this.$store.state.Analytics[AnalyticsType.ADVANCED].data,
          telegram: (this.$store.state.Analytics[AnalyticsType.ADVANCED].data.telegram || []).map(
            (item) => ({
              ...item,
              service: 'telegram',
            })
          ),
          google: (this.$store.state.Analytics[AnalyticsType.ADVANCED].data.google || []).map(
            (item) => ({
              ...item,
              service: 'google',
            })
          ),
        } || {}
      );
    },
    analyticsSelectedOption() {
      return this.$store.state.Analytics[AnalyticsType.ADVANCED].selectedOption;
    },
    selectedServices() {
      return this.$store.state.Analytics[AnalyticsType.ADVANCED].selectedServices;
    },
    selectedAccounts() {
      return this.$store.state.Analytics[AnalyticsType.ADVANCED].selectedAccounts;
    },
    selectedCompanies() {
      return this.$store.state.Analytics[AnalyticsType.ADVANCED].selectedCompanies;
    },
    previousAccountsData() {
      return this.$store.state.Analytics[AnalyticsType.ADVANCED].previousAccountsData;
    },
    previousCompaniesData() {
      return this.$store.state.Analytics[AnalyticsType.ADVANCED].previousCompaniesData;
    },

    // --- DATA PROCESSING ---
    currentStats() {
      if (!this.selectedServices.length) {
        // Используем данные Overview, но приводим к нужной структуре
        const overview = Object.entries(
          this.$store.state.Analytics[AnalyticsType.OVERVIEW].data || {}
        );
        return overview.flatMap(([service, arr]) =>
          arr.map((item) => ({
            ...item,
            id: service,
            name: getServiceName(service),
            for_date: item.for_date,
            service: service,
          }))
        );
      }
      return this.getCurrentStatsArray();
    },
    hasValue() {
      if (!this.selectedServices.length) {
        // Проверяем данные Overview
        return Object.values(this.$store.state.Analytics[AnalyticsType.OVERVIEW].data || {}).some(
          (arr) => arr.length > 0
        );
      }
      // Проверяем данные Advanced
      return Object.values(this.advancedAnalyticsData).some((arr) => arr.length > 0);
    },
    itemsMap() {
      const map = {};
      this.currentStats.forEach((item) => {
        if (item.id != null) {
          if (!map[item.id]) {
            map[item.id] = { name: item.name, dataByDate: {} };
          }
          const kpiKey = KPIKeysBackendMapper[this.analyticsSelectedOption];
          map[item.id].dataByDate[item.for_date] =
            (map[item.id].dataByDate[item.for_date] || 0) + (Number(item[kpiKey]) || 0);
        }
      });
      return map;
    },
    uniqueIds() {
      return Object.keys(this.itemsMap);
    },
    isAggregatedView() {
      return this.uniqueIds.length > this.MAX_ITEMS;
    },

    // --- CHART CONFIG ---
    analyticsLineChartTitle() {
      const kpiName = GenitiveKPIText[this.analyticsSelectedOption] || '';
      return `Динамика изменения ${kpiName}`;
    },
    platformsTitle() {
      if (this.isAggregatedView || this.uniqueIds.length === 0) {
        return '';
      }
      if (this.selectedServices.length > 1) {
        return 'Сервисы';
      }
      if (this.selectedCompanies.length) {
        return 'Кампании';
      }
      // If accounts are selected, the next level is either companies (if they exist) or just accounts
      if (this.selectedAccounts.length) {
        const serviceKey = this.selectedServices[0];
        const hasCompanyData = this.previousCompaniesData?.[serviceKey]?.length > 0;
        return hasCompanyData ? 'Кампании' : 'Аккаунты';
      }
      // If only service is selected, the next level is accounts
      return 'Аккаунты';
    },
    lineChartData() {
      const allDates = [...new Set(this.currentStats.map((item) => item.for_date))].sort();
      let datasets = [];

      if (this.isAggregatedView) {
        datasets = this.getAggregatedDataset(allDates);
      } else if (this.selectedServices.length > 1) {
        datasets = this.getMultiServiceDatasets(allDates);
      } else {
        datasets = this.getDetailedDatasets(allDates);
      }

      return {
        labels: allDates.map((d) => moment(d).format('DD MMM')),
        datasets,
      };
    },
    platforms() {
      if (this.isAggregatedView) {
        return [this.getAggregatedPlatform()];
      }
      if (this.selectedServices.length > 1) {
        return this.selectedServices.map((service) => ({
          name: getServiceName(service),
          borderColor: this.getColorCached(service),
        }));
      }
      return this.uniqueIds.map((id) => ({
        name: this.itemsMap[id].name,
        borderColor: this.getColorCached(id),
      }));
    },
    analyticsStatsData() {
      return Object.keys(KPIKeys).map((kpiKey) => {
        const backendKey = KPIKeysBackendMapper[kpiKey];
        const calculatedValue = this.calculateKpi(this.currentStats, backendKey);

        let value;
        if (kpiKey === KPIKeys.CTR) {
          value = calculatedValue + '%';
        } else if (kpiKey === KPIKeys.AVERAGE_CPC || kpiKey === KPIKeys.EXPENSES) {
          // Определяем валюту для отображения
          let currency = 'RUB'; // по умолчанию
          if (this.onlyTelegramSelected) {
            currency = 'EUR';
          } else if (this.onlyGoogleSelected) {
            currency = 'USD';
          }
          value = formatCurrency(calculatedValue, currency);
        } else {
          value = this.numberWithSpacesCeilRound(calculatedValue).toLocaleString('ru-RU');
        }

        return {
          id: kpiKey,
          label: KPIKeys.AVERAGE_CPC === kpiKey ? ShortKPIText[kpiKey] : KPIText[kpiKey],
          value,
          change: null,
        };
      });
    },
  },
  methods: {
    formatCurrency,
    getCurrencyCode() {
      return (service) => {
        const s = service.toUpperCase();
        if (s === ServiceType.TELEGRAM) return 'EUR';
        if (s === ServiceType.GOOGLE) return 'USD';
        return 'RUB';
      };
    },
    getCurrentStatsArray() {
      const {
        selectedServices,
        selectedAccounts,
        selectedCompanies,
        advancedAnalyticsData,
        previousAccountsData,
        previousCompaniesData,
      } = this;

      if (!selectedServices.length) {
        return Object.values(advancedAnalyticsData).flat();
      }

      if (selectedServices.length > 1) {
        return selectedServices.reduce((acc, serviceKey) => {
          if (advancedAnalyticsData[serviceKey]) {
            acc.push(...advancedAnalyticsData[serviceKey]);
          }
          return acc;
        }, []);
      }

      const serviceKey = selectedServices[0];

      // This is the most detailed level. The API returns stats for specific campaigns.
      if (selectedCompanies.length) {
        return (advancedAnalyticsData[serviceKey] || []).filter((item) =>
          selectedCompanies.includes(String(item.id))
        );
      }

      // User selected an account. We should show data for its campaigns.
      // `previousCompaniesData` stores this level of detail.
      if (selectedAccounts.length) {
        if (
          previousCompaniesData &&
          previousCompaniesData[serviceKey] &&
          previousCompaniesData[serviceKey].length
        ) {
          return previousCompaniesData[serviceKey];
        }
        // If no campaign data, show account-level data.
        // `previousAccountsData` stores this.
        if (previousAccountsData && previousAccountsData[serviceKey]) {
          return previousAccountsData[serviceKey].filter((item) =>
            selectedAccounts.includes(item.id)
          );
        }
        // Fallback to whatever is in the main data object, filtered by account.
        return (advancedAnalyticsData[serviceKey] || []).filter((item) =>
          selectedAccounts.includes(item.account_id)
        );
      }

      // User selected only a service. Show data for its accounts.
      if (previousAccountsData && previousAccountsData[serviceKey]) {
        return previousAccountsData[serviceKey];
      }

      // Fallback for service-only selection
      return advancedAnalyticsData[serviceKey] || [];
    },

    createDataset(label, data, borderColor) {
      return {
        label,
        data,
        borderColor,
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.2,
        radius: 0,
      };
    },

    getAggregatedDataset(labels) {
      const aggregatedDataByDate = {};
      labels.forEach((date) => {
        aggregatedDataByDate[date] = 0;
        this.uniqueIds.forEach((id) => {
          aggregatedDataByDate[date] +=
            (this.itemsMap[id].dataByDate[date] && this.itemsMap[id].dataByDate[date].toFixed(2)) ||
            0;
        });
      });
      const platform = this.getAggregatedPlatform();
      const data = labels.map((date) => aggregatedDataByDate[date] || 0);
      return [this.createDataset(platform.name, data, platform.borderColor)];
    },

    getAggregatedPlatform() {
      const serviceKey = this.selectedServices[0];
      let name = 'Агрегированные данные';
      if (this.selectedCompanies.length) {
        const account = this.previousAccountsData?.[serviceKey]?.find(
          (acc) => acc.id === this.selectedAccounts[0]
        );
        name = account?.name || name;
      } else if (this.selectedAccounts.length) {
        const account = this.previousAccountsData?.[serviceKey]?.find(
          (acc) => acc.id === this.selectedAccounts[0]
        );
        name = account?.name || name;
      } else {
        name = getServiceName(serviceKey) || name;
      }
      return { name, borderColor: this.getColorCached(serviceKey) };
    },

    getDetailedDatasets(labels) {
      return this.uniqueIds.map((id) => {
        const item = this.itemsMap[id];
        const data = labels.map(
          (date) => (item.dataByDate[date] && item.dataByDate[date].toFixed(2)) || 0
        );
        const borderColor = this.getColorCached(id);
        return this.createDataset(item.name, data, borderColor);
      });
    },

    getMultiServiceDatasets(labels) {
      const kpiKey = KPIKeysBackendMapper[this.analyticsSelectedOption];
      return this.selectedServices.map((service) => {
        const serviceData = this.advancedAnalyticsData[service] || [];
        const dataByDate = {};
        serviceData.forEach((item) => {
          dataByDate[item.for_date] =
            (dataByDate[item.for_date] || 0) + (Number(item[kpiKey]) || 0);
        });
        const data = labels.map((date) => (dataByDate[date] && dataByDate[date].toFixed(2)) || 0);
        const name = getServiceName(service);
        const borderColor = this.getColorCached(service);
        return this.createDataset(name, data, borderColor);
      });
    },

    calculateKpi(stats, kpiKey) {
      if (!stats || stats.length === 0) return 0;

      const groupedStats = Map.groupBy(stats, (item) => item.service);
      const allItems = [];

      // Конвертируем валюты только для нескольких сервисов
      // Если 0 - значит все выбраны
      const isMultipleServices =
        this.selectedServices.length > 1 || this.selectedServices.length === 0;

      groupedStats.forEach((items, service) => {
        if (service === 'telegram' && isMultipleServices) {
          // Конвертируем Telegram из EUR в RUB только при нескольких сервисах
          const convertedItems = items.map((item) => ({
            ...item,
            total_cpc: Number(item.total_cpc || 0) * this.getExchangeRate('EUR', 'RUB'),
            total_spent: Number(item.total_spent || 0) * this.getExchangeRate('EUR', 'RUB'),
          }));
          allItems.push(...convertedItems);
        } else if (service === 'google' && isMultipleServices) {
          // Конвертируем Google из USD в RUB только при нескольких сервисах
          const convertedItems = items.map((item) => ({
            ...item,
            total_cpc: Number(item.total_cpc || 0) * this.getExchangeRate('USD', 'RUB'),
            total_spent: Number(item.total_spent || 0) * this.getExchangeRate('USD', 'RUB'),
          }));
          allItems.push(...convertedItems);
        } else {
          // Один сервис (myTarget, VK, Yandex и др.) или остальные сервисы - не конвертируем
          // Они уже работают в рублях
          allItems.push(...items);
        }
      });

      if (kpiKey === 'total_ctr') {
        const clicks = allItems.reduce((sum, item) => sum + Number(item.total_clicks ?? 0), 0);
        const views = allItems.reduce((sum, item) => sum + Number(item.total_views ?? 0), 0);
        const ctr = views ? (clicks / views) * 100 : 0;
        return ctr.toFixed(2);
      }

      if (kpiKey === 'total_cpc') {
        const spent = allItems.reduce((sum, item) => sum + Number(item.total_spent ?? 0), 0);
        const clicks = allItems.reduce((sum, item) => sum + Number(item.total_clicks ?? 0), 0);
        const cpc = clicks ? spent / clicks : 0;
        return cpc;
      }

      return allItems.reduce((sum, item) => {
        let val = item[kpiKey];
        if (typeof val === 'string') val = parseFloat(val.replace(',', '.'));
        if (isNaN(val)) val = 0;
        return sum + (val || 0);
      }, 0);
    },

    getExchangeRate(from, to) {
      if (!this.currencyRates) return 1;
      const rate = this.currencyRates.find((el) => el.from === from && el.to === to);
      return rate ? rate.value : 1;
    },

    shouldConvertCurrency(kpiKey) {
      // Конвертируем только для денежных показателей
      return kpiKey === 'total_spent' || kpiKey === 'total_cpc';
    },
    getColorCached(key) {
      const isRandom = this.selectedServices.length === 1;
      if (!key) key = 'default';
      const cacheKey = isRandom ? `random_${key}` : key;
      if (!this.colorsMap.has(cacheKey)) {
        const color = isRandom ? getRandomHexColor() : getServiceColor(key);
        this.colorsMap.set(cacheKey, color);
      }
      return this.colorsMap.get(cacheKey);
    },
  },
  watch: {
    activeAccount: {
      async handler() {
        if (this.activeAccount && !this.currencyRates) {
          await this.$store.dispatch('Auth/getCurrencyRates', {
            business_account_id: this.activeAccount.id,
          });
        }
      },
    },
  },
};
</script>
<style lang="scss" scoped>
.analytics-advanced {
  &__stats-panel {
    margin-top: 20px;
  }

  &__line-chart {
    margin-top: 40px;
  }

  &__placeholder {
    margin-top: 50px;
  }
}
</style>
